import React, { Fragment } from 'react';
import { useNode, BuilderComponent, getFontStyle, DesignMode } from '@mnz/builder';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { AccordionSummary } from '../../common/components/mui/Accordion';
import Link from '../../common/components/link/DesignLink';
import { ContactSetting } from './ContactSetting';
import { getBorderCSS, hoverAnimationCss } from '../../common/style';
import LabelIcon from '../../common/components/label-icon/LabelIcon';
import StyledComponent from '../../core/StyledComponent';
import { ContactSettingProps } from './ContactSetting';

export const Contact: BuilderComponent<ContactSettingProps> = ({ designSettings, account, hiddenContactItems }) => {
    const {
        connectors: { connect, drag },
        query: { getOptions },
        hidden,
    } = useNode((node) => ({
        hidden: node.data.hidden,
    }));

    const canEdit = getOptions().enabled;

    const CONTACTS = [
        {
            value: account.phone,
            type: 'phone',
            url: account.phone,
            icon: 'Phone',
            isHidden: hiddenContactItems?.PHONE,
        },
        {
            value: account.email,
            type: 'email',
            url: account.email,
            icon: 'Email',
            isHidden: hiddenContactItems?.EMAIL,
        },
        {
            value: account.website,
            type: 'website',
            url: account.website,
            icon: 'Web',
            isHidden: hiddenContactItems?.WEBSITE,
        },
        {
            value: account.customAddress ? account.customAddress : account.address,
            type: 'address',
            url: `${account.address}`,
            icon: 'Location',
            isHidden: hiddenContactItems?.LOCATION,
        },
    ];

    return (
        <Box>
            {!hidden && (
                <Box
                    sx={{
                        marginX: '35px',
                        marginTop: '16px',
                        ...hoverAnimationCss({ content: '"Contact & Location"', active: canEdit }),
                    }}
                >
                    <Accordion
                        square
                        sx={{
                            color: designSettings.colors.buttonText.color,
                            backgroundColor: 'inherit',
                            ...getFontStyle(designSettings, 'secondary'),
                            ...getBorderCSS({
                                color: designSettings.colors.button.color,
                                buttonStyle: designSettings.styles.button,
                                corner: designSettings.styles.corner,
                                maxBorderRadius: 1.5,
                            }),
                        }}
                    >
                        <AccordionSummary sx={{ paddingLeft: '40px', paddingRight: '20px' }}>
                            <Typography
                                sx={{
                                    ...getFontStyle(designSettings, 'secondary', 'bold'),
                                    color: designSettings.colors.buttonText.color,
                                    width: '100%',
                                    textAlign: 'center',
                                }}
                            >
                                Contact
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <StyledComponent ref={(ref) => connect(drag(ref))}>
                                {CONTACTS.map((contact) => (
                                    <Fragment key={contact.type}>
                                        {contact.value && !contact.isHidden && (
                                            <Link
                                                type={contact.type}
                                                url={contact.url}
                                                isLiveMode={getOptions().mode === DesignMode.LIVE}
                                            >
                                                <Box
                                                    sx={{
                                                        ...getBorderCSS({
                                                            color: designSettings.colors.buttonText.color,
                                                            buttonStyle: 'outline',
                                                            corner: designSettings.styles.corner,
                                                        }),
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '10px',
                                                        justifyContent: 'start',
                                                        cursor: 'pointer',
                                                        ':hover': {
                                                            opacity: canEdit ? 1 : 0.7,
                                                        },
                                                        mb: '10px',
                                                    }}
                                                >
                                                    <Box>
                                                        <LabelIcon
                                                            type={designSettings.styles.icon}
                                                            icon={contact.icon}
                                                            size={25}
                                                            color={designSettings.colors.buttonText.color}
                                                            corner={designSettings.styles.corner}
                                                        />
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            marginLeft: '10px',
                                                            textAlign: 'left',
                                                            lineHeight: '20px',
                                                        }}
                                                    >
                                                        {contact.value}
                                                    </Box>
                                                </Box>
                                            </Link>
                                        )}
                                    </Fragment>
                                ))}
                            </StyledComponent>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}
        </Box>
    );
};

Contact.defaultProps = {
    hiddenContactItems: {},
};

Contact.settings = {
    displayName: 'Contact',
    related: {
        settings: ContactSetting,
    },
    rules: {
        canDrag: () => false,
    },
    custom: {
        showBox: false,
    },
};
